import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Alert, Button, Card, Checkbox, Field, Select, Spinner, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { ApiError, api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { usePeople, useProjectForm } from './data';
import { type Priority, PRIORITIES, priorityName } from './requests-data';

interface HighPriorityConflict {
  code: 'high_priority_exists';
  message: string;
  projects: Array<{ number: number; code: string; name: string }>;
}

/** Alta directa de un proyecto (quien gestiona proyectos): se precarga con la plantilla de fases de su tipo. */
export function NewProjectPage() {
  const form = useProjectForm();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [typeId, setTypeId] = useState('');
  const [priority, setPriority] = useState<Priority>('media');
  const [ownerId, setOwnerId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [labels, setLabels] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<HighPriorityConflict | null>(null);
  const [busy, setBusy] = useState(false);
  const orgId = organizationId ?? form.data?.organizations[0]?.id ?? null;
  const people = usePeople(orgId);

  if (!form.data) return form.error ? <Alert>{errorMessage(form.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  if (form.data.organizations.length === 0) return <p className="text-sm text-slate-600">{t('acceso.sin_permiso')}</p>;
  const data = form.data;

  async function create(confirmHighPriority = false) {
    setBusy(true);
    setError(null);
    try {
      const created = await api<{ number: number }>('/api/proyectos', {
        json: {
          organizationId: orgId,
          name: name.trim(),
          description: description.trim() || null,
          projectTypeId: typeId || null,
          priority,
          ownerId: ownerId || null,
          startDate: startDate || null,
          dueDate: dueDate || null,
          labelIds: labels,
          confirmHighPriority,
        },
      });
      await qc.invalidateQueries({ queryKey: ['proyectos'] });
      void navigate(`/proyectos/${created.number}`, { state: { created: true } });
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as Partial<HighPriorityConflict> | undefined) : undefined;
      if (err instanceof ApiError && err.status === 409 && body?.code === 'high_priority_exists') setConflict(body as HighPriorityConflict);
      else setError(errorMessage(err));
      setBusy(false);
    }
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void create();
  };

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <Link to="/proyectos" className="text-sm text-marca hover:underline">
          ← {t('pr.titulo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">{t('pr.nuevo')}</h1>
        <p className="text-sm text-slate-600">{t('pr.nuevo_ayuda')}</p>
      </div>
      <Card>
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          {data.organizations.length > 1 && (
            <Select
              id="pr-org"
              label={t('pr.organizacion')}
              value={orgId ?? ''}
              onChange={(e) => {
                setOrganizationId(e.target.value);
                setOwnerId('');
              }}
            >
              {data.organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.groupName} › {o.name}
                </option>
              ))}
            </Select>
          )}
          <Field id="pr-nombre" label={t('pr.nombre')} required minLength={3} maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
          <Textarea id="pr-descripcion" label={t('pr.descripcion_campo')} rows={4} maxLength={10_000} value={description} onChange={(e) => setDescription(e.target.value)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select id="pr-tipo" label={t('pr.tipo')} value={typeId} onChange={(e) => setTypeId(e.target.value)}>
              <option value="">{t('pr.tipo_ninguno')}</option>
              {data.projectTypes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
            <Select id="pr-prioridad" label={t('pr.prioridad')} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {priorityName(p)}
                </option>
              ))}
            </Select>
            <Select id="pr-responsable" label={t('pr.responsable')} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
              <option value="">{t('pr.sin_responsable')}</option>
              {people.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName}
                </option>
              ))}
            </Select>
            <div />
            <Field id="pr-inicio" type="date" label={t('pr.inicio')} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <Field id="pr-vence" type="date" label={t('pr.vence')} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
          <p className="text-xs text-slate-500">{typeId ? t('pr.plantilla_ayuda') : t('pr.sin_plantilla_ayuda')}</p>
          {data.projectLabels.length > 0 && (
            <fieldset className="space-y-1">
              <legend className="text-sm font-medium text-slate-700">{t('pr.etiquetas')}</legend>
              {data.projectLabels.map((l) => (
                <Checkbox
                  key={l.id}
                  label={l.name}
                  checked={labels.includes(l.id)}
                  onChange={(e) => setLabels(e.target.checked ? [...labels, l.id] : labels.filter((x) => x !== l.id))}
                />
              ))}
            </fieldset>
          )}
          {conflict ? (
            <Alert tone="warning">
              <p>{conflict.message}</p>
              <ul className="mt-1 list-disc pl-5">
                {conflict.projects.map((p) => (
                  <li key={p.number}>
                    {p.code} · {p.name}
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex gap-2">
                <Button disabled={busy} onClick={() => void create(true)}>
                  {t('sp.confirmar_alta')}
                </Button>
                <Button variant="secondary" onClick={() => setConflict(null)}>
                  {t('comun.cancelar')}
                </Button>
              </div>
            </Alert>
          ) : (
            <div className="flex gap-2">
              <Button type="submit" disabled={busy || name.trim().length < 3}>
                {t('pr.crear')}
              </Button>
              <Link to="/proyectos" className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                {t('comun.cancelar')}
              </Link>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
}
